import React from 'react';
import ReactMarkdown from 'react-markdown';
import { ResearchCompletedData } from '../types/research';

interface ResearchReportProps {
  report: ResearchCompletedData | null;
  isVisible: boolean;
}

const ResearchReport: React.FC<ResearchReportProps> = ({ report, isVisible }) => {
  if (!isVisible || !report) {
    return null;
  }

  return (
    <div className="w-full max-w-4xl mx-auto mb-8">
      {/* Summary Card */}
      <div className="bg-green-50 rounded-lg shadow-sm border border-green-200 mb-6 overflow-hidden">
        <div className="bg-green-100 px-6 py-4 border-b border-green-200">
          <h3 className="text-lg font-semibold text-green-900 flex items-center">
            <span className="mr-2">📋</span>
            Research Summary
          </h3>
        </div>
        <div className="px-6 py-4">
          <p className="text-green-800 leading-relaxed">{report.short_summary}</p>
        </div>
      </div>

      {/* Full Report */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">📄</span>
            Full Research Report
          </h3>
        </div>
        <div className="px-6 py-6">
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mb-4">{children}</h1>,
                h2: ({ children }) => <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-6">{children}</h2>,
                h3: ({ children }) => <h3 className="text-lg font-medium text-gray-700 mb-2 mt-4">{children}</h3>,
                p: ({ children }) => <p className="text-gray-700 mb-4 leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>,
                li: ({ children }) => <li className="text-gray-700">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                em: ({ children }) => <em className="italic text-gray-800">{children}</em>,
                code: ({ children }) => (
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800">
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4">
                    <code className="text-sm font-mono text-gray-800">{children}</code>
                  </pre>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-blue-200 pl-4 py-2 mb-4 bg-blue-50 italic text-gray-700">
                    {children}
                  </blockquote>
                ),
              }}
            >
              {report.markdown_report}
            </ReactMarkdown>
          </div>
        </div>
      </div>

      {/* Follow-up Questions */}
      {report.follow_up_questions && report.follow_up_questions.length > 0 && (
        <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-200 overflow-hidden">
          <div className="bg-blue-100 px-6 py-4 border-b border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 flex items-center">
              <span className="mr-2">🤔</span>
              Follow-up Questions
            </h3>
          </div>
          <div className="px-6 py-4">
            <ul className="space-y-3">
              {report.follow_up_questions.map((question, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center">
                      <span className="text-blue-700 font-semibold text-xs">{index + 1}</span>
                    </div>
                  </div>
                  <p className="text-blue-800 leading-relaxed">{question}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchReport; 